import { excelSerialToJsDate, javascriptDateToMMDDYYYY, javascriptDateToYYYYMMDD } from '../helpers/date-helpers';
import { isSupportedDataType, isValidDateInput, isValidExchangeTickerSymbol } from '../helpers/args-validators';
import { FunctionCallCache } from '../helpers/functionCallCache';
import { FunctionLocationTracker } from '../helpers/functionLocationTracker';
import { KeyBuilder } from '../helpers/keyBuilder';
const SUPPORTED_DATETYPES = ['Closing_Index_Value'];
function normalizeLabel(x) {
    return String(x ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ')
        .replace(/[_\-]/g, ' ');
}
function parseDateCellToYYYYMMDD(cell) {
    if (cell == null || cell === '')
        return null;
    const s = String(cell).trim();
    // MM/DD/YYYY string case
    if (s.includes('/')) {
        if (!isValidDateInput(s))
            return null;
        const d = new Date(s);
        return javascriptDateToYYYYMMDD(d);
    }
    // Excel serial case
    const n = Number(cell);
    if (Number.isFinite(n)) {
        const d = excelSerialToJsDate(n);
        return javascriptDateToYYYYMMDD(d);
    }
    // ISO-like fallback
    const iso = s.slice(0, 10);
    if (/^\d{4}-\d{2}-\d{2}$/.test(iso))
        return iso;
    return null;
}
/**
 * Finds "label -> value" style rows inside a selected input range
 * Example:
 *   Start Date | 1/3/2014
 */
function findValueByLabel(inputs, labelCandidates) {
    const wanted = new Set(labelCandidates.map((x) => normalizeLabel(x)));
    for (const row of inputs ?? []) {
        if (!row || row.length < 2)
            continue;
        const label = normalizeLabel(row[0]);
        if (!wanted.has(label))
            continue;
        return row[2];
    }
    return null;
}
/**
 * Extract symbols from the Exchange/Ticker constituent grid.
 * Based on the template screenshot: a header row with "Exchange" and "Ticker"
 * and then rows below it of exchange/ticker values.
 */
function extractSymbolsFromExchangeTickerGrid(inputs) {
    const symbols = [];
    const seen = new Set();
    for (let r = 0; r < (inputs?.length ?? 0); r++) {
        for (let c = 0; c < (inputs[r]?.length ?? 0); c++) {
            const cell = String(inputs[r][c] ?? '')
                .trim()
                .toLowerCase();
            if (cell !== 'exchange')
                continue;
            const nextCell = String(inputs[r][c + 1] ?? '')
                .trim()
                .toLowerCase();
            if (nextCell !== 'ticker')
                continue;
            // Parse rows underneath header
            for (let rr = r + 1; rr < inputs.length; rr++) {
                const exchange = String(inputs[rr][c] ?? '').trim();
                const ticker = String(inputs[rr][c + 1] ?? '').trim();
                // stop at first fully blank row
                if (!exchange && !ticker)
                    break;
                // skip incomplete row
                if (!exchange || !ticker)
                    continue;
                const sym = `${exchange.toUpperCase()}:${ticker.toUpperCase()}`;
                if (!isValidExchangeTickerSymbol(sym))
                    continue;
                if (!seen.has(sym)) {
                    seen.add(sym);
                    symbols.push(sym);
                }
            }
            return symbols;
        }
    }
    return symbols;
}
function normalizeSharePriceType(x) {
    // Examples from template:
    // "Adjusted_Closing_Share_Price" -> "adjusted_closing_share_price"
    // "Adjusted Closing Share Price" -> "adjusted_closing_share_price"
    return String(x ?? '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, '_');
}
function normalizeIndexWeighting(x) {
    // Examples from template:
    // "Equal Weighted" -> "equal_weighted"
    // "Market Cap Weighted" -> "market_cap_weighted"
    const v = String(x ?? '')
        .trim()
        .toLowerCase();
    if (v.includes('equal'))
        return 'equal_weighted';
    if (v.includes('market'))
        return 'market_cap_weighted';
    // allow already correct values
    if (v === 'equal_weighted' || v === 'market_cap_weighted')
        return v;
    return v.replace(/\s+/g, '_');
}
/**
 * Parses the user-selected Inputs range (Custom_Index_Inputs_1)
 * into the backend inputs structure expected by getCustomBuiltIndex tool/service.
 */
function parseCustomBuiltIndexInputs(inputs) {
    const symbols = extractSymbolsFromExchangeTickerGrid(inputs);
    if (symbols.length === 0) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'No symbols found in the Index Constituents table (expected Exchange + Ticker columns).');
    }
    // Label/value section
    const startRaw = findValueByLabel(inputs, ['Start Date']) ?? null;
    const endRaw = findValueByLabel(inputs, ['End Date']) ?? null;
    const sharePriceTypeRaw = findValueByLabel(inputs, ['Share Price Type', 'Share Price Datatype']) ?? 'Adjusted_Closing_Share_Price';
    const currencyRaw = findValueByLabel(inputs, ['Currency']) ?? 'USD';
    const weightingRaw = findValueByLabel(inputs, ['Index Weighting', 'Weighting']) ?? 'Equal Weighted';
    const startValueRaw = findValueByLabel(inputs, ['Index Start Value', 'Start Value']) ?? 100;
    const startDate = parseDateCellToYYYYMMDD(startRaw);
    const endDate = parseDateCellToYYYYMMDD(endRaw);
    if (!startDate) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Start Date not found or invalid in the inputs range.');
    }
    if (!endDate) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'End Date not found or invalid in the inputs range.');
    }
    const indexStartValue = Number(startValueRaw);
    if (!Number.isFinite(indexStartValue) || indexStartValue <= 0) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Index Start Value is invalid (must be a positive number).');
    }
    return {
        startDate,
        endDate,
        sharePriceType: normalizeSharePriceType(sharePriceTypeRaw),
        currency: String(currencyRaw ?? '')
            .trim()
            .toUpperCase(),
        indexWeighting: normalizeIndexWeighting(weightingRaw),
        indexStartValue,
        symbols,
    };
}
function validateArgs(inputs, dataType, date) {
    if (!inputs || !Array.isArray(inputs) || inputs.length === 0) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid inputs range (must be a non-empty range).');
    }
    if (!isSupportedDataType(SUPPORTED_DATETYPES, dataType)) {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, `Invalid data type. Supported: ${SUPPORTED_DATETYPES.join(', ')}`);
    }
    // date is NOT optional (per your note)
    if (date == null || String(date).trim() === '') {
        throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Date is required (MM/DD/YYYY or Excel serial date).');
    }
    // Validate date format
    const s = String(date).trim();
    if (s.includes('/')) {
        if (!isValidDateInput(s)) {
            throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid date (expected MM/DD/YYYY or Excel serial date).');
        }
    }
    else {
        const n = Number(date);
        if (!Number.isFinite(n)) {
            throw new CustomFunctions.Error(CustomFunctions.ErrorCode.invalidValue, 'Invalid date (expected MM/DD/YYYY or Excel serial date).');
        }
    }
    return true;
}
let delegate = () => {
    throw new CustomFunctions.Error(CustomFunctions.ErrorCode.notAvailable, 'Initializing… press F9 to recalc.');
};
export function setCustomBuiltIndexDelegate(fn) {
    delegate = fn;
}
export function customBuiltIndexWrapper(inputs, dataType, date, invocation) {
    return delegate(inputs, dataType, date, invocation);
}
/**
 * Excel entrypoint implementation:
 * - Parse the selected input range into params.inputs
 * - Parse date into YYYY-MM-DD (required)
 * - Build a stable cache key
 * - Register for refresh / tracking
 */
export function customBuiltIndexImpl(inputs, dataType, date, invocation) {
    validateArgs(inputs, dataType, date);
    const parsedInputs = parseCustomBuiltIndexInputs(inputs);
    // Required single-date request
    const javascriptDate = String(date).includes('/') ? new Date(date) : excelSerialToJsDate(Number(date));
    const queryDate = javascriptDateToYYYYMMDD(javascriptDate);
    const cache = FunctionCallCache.getInstance();
    const tracker = FunctionLocationTracker.getInstance();
    const args = {
        dataType,
        date: queryDate,
        inputs: parsedInputs,
    };
    const key = KeyBuilder.build('custom_built_index', args);
    cache.registerByKey(key, { functionName: 'custom_built_index', params: args });
    if (invocation?.address) {
        // We can't reconstruct the full input-range formula reliably in a string,
        // but we can show a readable placeholder.
        tracker.registerCell(key, invocation.address, `=ALPHA.CUSTOM_BUILT_INDEX(<inputs_range>, "${javascriptDateToMMDDYYYY(javascriptDate)}", "${dataType}")`);
        cache.setAutoRefresh(key);
    }
    return cache.getValueForExcelByKey(key);
}
//# sourceMappingURL=customBuiltIndex.js.map